import { ChangeEventHandler, useState } from 'react'

import classNames from 'classnames'
import { find, isBoolean, isEqual } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { SingleValue } from 'react-select'
import { connect, DataItem, useDataItem } from 'scrivito'

import { FilterSelect } from './FilterSelect'
import { useContactTabData } from './useContactTabData'
import { LIST_SIZE } from '../../const'
import { TabEmptyState } from '../TabEmptyState/TabEmptyState'
import { StringText } from '@/Atomic/Atoms/StringText'
import { Button } from '@/Atomic/Molecules/Button'
import { Search } from '@/Atomic/Molecules/Inputs/Search'
import { Option } from '@/Atomic/Molecules/Inputs/Select/types'
import { SimpleSelect } from '@/Atomic/Molecules/SimpleSelect'
import {
  SublistBody,
  SublistSearchResult,
} from '@/Atomic/Organisms/RelationalSublist'
import { ScrollablePanel } from '@/Atomic/Templates/ScrollablePanel'
import { Sublist, SublistFooter } from '@/Atomic/Templates/Sublist'
import { DataClassDrawer } from '@/Atomic/Widgets/DataClassDrawer/DataClass.Drawer'
import { DataItemDrawer } from '@/Atomic/Widgets/DataItemDrawer'
import { errorToast } from '@/Components/Toast'
import { Company } from '@/Data/Company/CompanyDataClass'
import {
  CONTACT_ASC_ORDER,
  CONTACT_DESC_ORDER,
  DataClassName,
} from '@/Data/const'
import { ContactContactRelation } from '@/Data/ContactContactRelation/ContactContactRelationDataClass'
import { ContactDocumentRelation } from '@/Data/ContactDocumentRelation/ContactDocumentRelationDataClass'
import { ContactProjectRelation } from '@/Data/ContactProjectRelation/ContactProjectRelationDataClass'
import { Person } from '@/Data/Person/PersonDataClass'
import { DataScopeOrder } from '@/Data/types'
import { useFlashSublistItem } from '@/hooks/useFlashSublistItem'
import { useModalHandler } from '@/hooks/useModalHandler'
import {
  useDataItemDrawer,
  useDrawer,
} from '@/utils/CRUD/hooks/useDataItemDrawer'
import { getEntityLabelKey } from '@/utils/getEntityLabelKey'
import { reportError } from '@/utils/reportError'

interface ContactTabProps {
  searchInputName: string
  contactRelation?: 'contact' | 'project' | 'document'
  contactEntity?: 'Person' | 'Company'
}

export const ContactTab: React.FC<ContactTabProps> = connect(
  ({
    searchInputName,
    contactRelation = 'contact',
    contactEntity = 'Person',
  }) => {
    const { t } = useTranslation()
    const dataItem = useDataItem()
    const subjectId = dataItem?.id() ?? ''

    const [search, setSearch] = useState('')
    const [order, setOrder] = useState<DataScopeOrder>(CONTACT_DESC_ORDER)
    const [role, setRole] = useState<string | null>(null)
    const [contactType, setContactType] = useState<string | null>(null)
    const [relatedId, setRelatedId] = useState('')

    const { flashId: flashItemId, flash: flashItemIdNow } =
      useFlashSublistItem()
    const { drawerItem, setDrawerItem, isDrawerOpen, openDrawer, closeDrawer } =
      useDataItemDrawer()
    const {
      isDrawerOpen: isRelationDrawerOpen,
      openDrawer: openRelationDrawer,
      closeDrawer: closeRelationDrawer,
    } = useDrawer()
    const {
      isOpen: isCreateOpen,
      openModal: openCreateDrawer,
      closeModal: closeCreateDrawer,
    } = useModalHandler()

    const onSearchChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      setSearch(event.target.value)
    }

    const orderOptions: Option[] = [
      { label: t('common.sort.recentFirst'), value: 'desc' },
      { label: t('common.sort.recentLast'), value: 'asc' },
    ]
    const currentOrderValue = isEqual(order, CONTACT_DESC_ORDER)
      ? 'desc'
      : 'asc'
    const selectedOrderOption = orderOptions.find(
      (o) => o.value === currentOrderValue,
    )
    const handleOrderChange = (newValue: SingleValue<Option | unknown>) => {
      const val = (newValue as Option)?.value
      setOrder(val === 'asc' ? CONTACT_ASC_ORDER : CONTACT_DESC_ORDER)
    }

    const {
      items,
      searchItems,
      hasMoreSearchResults,
      hasMore,
      loadMore,
      count,
    } = useContactTabData({
      subjectId,
      search,
      order,
      role,
      contactType,
      listSize: LIST_SIZE,
      contactRelation,
      contactEntity,
    })

    const linkContact = async (relatedId: string) => {
      try {
        if (contactRelation === 'project') {
          await ContactProjectRelation.create({
            project: subjectId,
            contact: relatedId,
          })
          flashItemIdNow(relatedId)
        } else if (contactRelation === 'document') {
          await ContactDocumentRelation.create({
            document: subjectId,
            contact: relatedId,
          })
          flashItemIdNow(relatedId)
        } else {
          setRelatedId(relatedId)
          openRelationDrawer()
        }
      } catch (error) {
        errorToast(error as Error)
        reportError(error)
      }
    }

    const handleContactCreated = (newContact: DataItem) => {
      closeCreateDrawer()
      linkContact(newContact.id())
    }

    if (!subjectId) return

    const header = (
      <div className="tab-header-wrapper">
        <div className="search-filter">
          <Search
            name={searchInputName}
            onChange={onSearchChange}
            searchWrapperClassName="tab-header-search"
            placeholder={t('search.title')}
          />
          <StringText value={t('common.sortBy')} />
          <SimpleSelect
            name="order-select"
            className="small"
            options={orderOptions}
            value={selectedOrderOption}
            onChange={handleOrderChange}
          />
          {contactRelation === 'contact' && (
            <>
              <StringText value={t('common.filterBy')} />
              <FilterSelect
                dataClassName="ContactContactRelation"
                filterKey="role"
                allOptionLabel={t('common.filter.allRoles')}
                value={role}
                setValue={setRole}
              />
              <FilterSelect
                dataClassName="ContactContactRelation"
                filterKey="contactType"
                allOptionLabel={t('common.filter.allContactTypes')}
                value={contactType}
                setValue={setContactType}
              />
            </>
          )}
        </div>
        {contactRelation === 'contact' && (
          <div className="tab-header-actions">
            <Button
              title={t('common.create')}
              size="sm"
              iconSize="sm"
              iconColor="white"
              icon="add"
              onClick={openCreateDrawer}
            />
          </div>
        )}
      </div>
    )

    const isItemsEmpty = items.length === 0

    return (
      <>
        <Sublist>
          <ScrollablePanel header={header}>
            {isItemsEmpty && !search && contactRelation === 'contact' ? (
              <div className="sublist-wrapper">
                <TabEmptyState
                  title={t(
                    contactEntity === 'Company'
                      ? 'tabs.empty.companies.title'
                      : 'tabs.empty.contacts.title',
                  )}
                  description={t(
                    contactEntity === 'Company'
                      ? 'tabs.empty.companies.description'
                      : 'tabs.empty.contacts.description',
                  )}
                  cta={{
                    label: t('common.create'),
                    icon: 'add',
                    onClick: openCreateDrawer,
                  }}
                />
              </div>
            ) : (
              <div
                className={classNames({
                  'sublist-wrapper': !isItemsEmpty || searchItems.length > 0,
                })}
              >
                <SublistBody
                  items={items}
                  flashItemId={flashItemId}
                  onOpenDrawer={(item) => {
                    setDrawerItem(item.dataItem)
                    openDrawer()
                  }}
                />
                <SublistSearchResult
                  items={searchItems}
                  link={linkContact}
                  flashItemId={flashItemId}
                />
                {search &&
                  !hasMoreSearchResults &&
                  contactRelation === 'contact' && (
                    <div className="sublist-create-prompt">
                      <StringText value={t('tabs.searchCreate.contacts')} />
                      <Button
                        variant="outlined"
                        icon="add"
                        onClick={openCreateDrawer}
                      >
                        {t('common.create')}
                      </Button>
                    </div>
                  )}
                {!search && (
                  <SublistFooter
                    amount={items.length}
                    total={count}
                    labelKey={getEntityLabelKey(
                      contactEntity === 'Company'
                        ? DataClassName.COMPANY
                        : DataClassName.PERSON,
                    )}
                    onLoadMore={hasMore ? loadMore : undefined}
                  />
                )}
              </div>
            )}
          </ScrollablePanel>
        </Sublist>
        {drawerItem && (
          <DataItemDrawer
            key={drawerItem.id()}
            isOpen={isDrawerOpen}
            close={closeDrawer}
            dataItem={drawerItem}
          />
        )}
        <DataClassDrawer
          dataClass={ContactContactRelation}
          isOpen={isRelationDrawerOpen}
          close={closeRelationDrawer}
          onCreated={() => flashItemIdNow(relatedId)}
          attributeOptions={{
            father: { default: subjectId, readOnly: true },
            contact: { default: relatedId, readOnly: true },
            contactType: {
              limitEnumValuesTo: getPossibleContactTypeSelectKeys(
                dataItem,
                getRelatedItem(relatedId, searchItems),
              ),
            },
          }}
        />
        <DataClassDrawer
          dataClass={contactEntity === 'Company' ? Company : Person}
          isOpen={isCreateOpen}
          close={closeCreateDrawer}
          onCreated={handleContactCreated}
          navigateOnCreated={false}
        />
      </>
    )
  },
)

const getPossibleContactTypeSelectKeys = (
  father: DataItem | undefined,
  contact: DataItem | undefined,
) => {
  const selectKeys = []

  if (!contact?.get('isExternal') && isBoolean(contact?.get('isExternal'))) {
    selectKeys.push('myteam')
  }
  if (contact?.get('isExternal')) {
    selectKeys.push('external')
  }
  if (
    contact?.get('isExternal') &&
    father?.get('isExternal') &&
    ((contact.dataClassName() === DataClassName.PERSON &&
      father.dataClassName() === DataClassName.COMPANY) ||
      (contact.dataClassName() === DataClassName.COMPANY &&
        father.dataClassName() === DataClassName.PERSON))
  ) {
    selectKeys.push('staff')
  }

  return selectKeys
}

const getRelatedItem = (
  relatedId: string,
  searchItems: {
    dataItem: DataItem
  }[],
) => {
  return find(searchItems, (sItem) => relatedId === sItem.dataItem?.id())
    ?.dataItem
}
